import { Component, computed, inject, input } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import type { OnyxColor } from '@onyx/angular';
import {
  OnyxDataWidgetComponent,
  OnyxDatePipe,
  OnyxInformationHeadingComponent,
} from '@onyx/angular';
import { ScheduleComplianceHelper } from '../../../../../../../../common/helpers/schedule-compliance.helper';
import { DictionaryCode } from '../../../../../../../../common/enums/dictionary-code';
import { CommonHelper } from '../../../../../../../../common/helpers/common.helper';
import { DeadlineHelper } from '../../../../../../../../common/helpers/deadline.helper';
import { DiffPipe } from '../../../../../../../../common/pipes/diff.pipe';
import { DurationPipe } from '../../../../../../../../common/pipes/duration.pipe';
import { TelematicsStore } from '../../../../../../../../common/stores/telematics.store';
import type { SwapPoint } from '../../../../../../../fleet/common/interfaces/technical-point';
import { OrderPointStatus } from '../../../../../../../orders/common/enums/order-point-status';
import { SwapVehicleType } from '../../../../../enums/swap/swap-vehicle-type';
import type { Swap } from '../../../../../interfaces/swap/swap';
import { RouteHelper } from './../../../../../../../../common/helpers/route.helper';

@Component({
  selector: 'app-swap-modal-summary-item-status',
  imports: [
    OnyxInformationHeadingComponent,
    TranslatePipe,
    OnyxDataWidgetComponent,
    OnyxDatePipe,
    DiffPipe,
    DurationPipe,
  ],
  templateUrl: './swap-modal-summary-item-status.component.html',
  styleUrl: './swap-modal-summary-item-status.component.scss',
})
export class SwapModalSummaryItemStatusComponent {
  protected readonly commonHelper = inject(CommonHelper);
  private readonly telematicsStore = inject(TelematicsStore);

  public readonly type = input.required<SwapVehicleType>();
  public readonly swap = input.required<Swap>();
  public readonly swapPoint = input.required<SwapPoint>();
  public readonly identifierColor = input.required<OnyxColor | null>();

  protected readonly DictionaryCode = DictionaryCode;
  protected readonly OrderPointStatus = OrderPointStatus;

  protected status = computed(() => this.swapPoint().status);
  protected icon = computed(() =>
    RouteHelper.getStatusIcon(this.swapPoint(), this.swapPoint().status),
  );
  protected telematicsItem = computed(() =>
    this.telematicsStore.getItem(
      this.type() === SwapVehicleType.PRIMARY
        ? this.swap().vehicle.uuid
        : (this.swap()?.otherVehicle?.uuid ?? ''),
    ),
  );
  protected route = computed(() => this.telematicsItem()?.routePoints);

  protected compliance = computed(() =>
    this.route()
      ? RouteHelper.getScheduleCompliance(this.swapPoint(), this.route()!)
      : null,
  );
  protected complianceColor = computed(() =>
    this.compliance()
      ? ScheduleComplianceHelper.getColor(this.compliance()!)
      : 'gray',
  );

  protected realizationTime = computed(() =>
    RouteHelper.getPointRealizationTime(this.swapPoint()),
  );
  protected realizationLabel = computed(
    () =>
      (
        ({
          [OrderPointStatus.TO_DO]: 'labels.eta',
          [OrderPointStatus.IN_PROGRESS]: 'labels.from',
          [OrderPointStatus.WAITING]: 'labels.from',
        }) as Partial<Record<OrderPointStatus, string>>
      )[this.status()] ?? '',
  );

  protected deadlineData = computed(() => {
    const isToDo = this.status() === OrderPointStatus.TO_DO;
    const label = isToDo
      ? 'labels.arrivalDeadlineTime'
      : 'labels.departureDeadlineTime';

    const deadlines = DeadlineHelper.getPointDeadline(this.swapPoint());

    const deadline = isToDo ? deadlines?.arrival : deadlines?.departure;
    if (!deadline) return { deadline: null, label };

    return { deadline, label };
  });
}
